import { DOCUMENT } from '@angular/common';
import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { ApiService } from 'app/api.service';
import { AppService } from 'app/app.service';
import { UISidebarComponent } from 'app/ui/ui-sidebar.component';

/**
 * Needs Your Attention: everything waiting on somebody, across every CRM record they can see.
 *
 * The deal workspace already answers this for one deal. This answers it for all of them, and
 * follows the user around the app — which is why it lives in CoreModule rather than CrmModule.
 * The CRM module is lazy-loaded, so a widget declared there would not exist on the dashboard,
 * which is exactly where the design shows it.
 *
 * It shows two different objects deliberately kept apart. A **follow-up** is a note or meeting
 * with a date, and it completes and reopens. **Required work** is what an automation asked for
 * and is holding a deal until somebody does it: it has no date, it is resolved rather than
 * completed, and a `file` one refuses until a file is attached. Flattening the two into one
 * "task" would mean lying about one of them.
 */
@Component({
	selector: 'app-core-attention-widget',
	templateUrl: './core-attention-widget.component.html',
	styleUrls: ['./core-attention-widget.component.scss']
})
export class CoreAttentionWidgetComponent implements OnInit, OnDestroy {

	/**
	 * How often the list is re-read for changes this browser did not make.
	 *
	 * Two rates, because the panel and the pill are watched differently. Open in front of
	 * somebody it is a to-do list they are working through, and a minute-old answer is fine;
	 * collapsed it is one number in the corner, worth keeping honest but not worth three queries
	 * a minute for ever. The pill's rate is enforced by skipping ticks rather than by a second
	 * timer, so switching between the two takes effect at once.
	 */
	private readonly OPEN_POLL_MS = 60 * 1000;
	private readonly PILL_POLL_MS = 5 * 60 * 1000;

	// One composer for both entry points — the pill's + and the window's — so a draft cannot
	// exist twice with two different answers in it.
	@ViewChild('composer') composer: any;
	@ViewChild('drawer') drawer: UISidebarComponent;

	// Only so the tab can be measured; nothing is written through it.
	@ViewChild('tab') tabEl: ElementRef;

	rows: any[] = [];
	counts = { all: 0, open: 0, completed: 0 };
	loaded = false;
	disabled = false;

	/**
	 * Whether the last read of the list failed.
	 *
	 * Kept because an empty list and an unread one are not the same thing and used to look
	 * identical: the fail path set `loaded` and left `rows` empty, so a CRM that could not be
	 * reached rendered as "All caught up · Nothing is waiting on you right now" — the widget
	 * asserting the one thing it did not know. Whatever went wrong is still not worth an error
	 * over somebody else's screen, so it is said here, in the panel, with the way to try again
	 * next to it.
	 */
	failed = false;

	private pollTimer = null;
	private reloadTimer = null;

	// Long enough to register as a state the row passed through, short enough not to be a wait.
	private readonly SETTLE_MS = 1400;

	/**
	 * Which of the design's three states this is in.
	 *
	 * `pill` is the compact always-available form; `floating` is a window you can put where you
	 * like; `docked` is pinned to the right edge. They are one component rather than three
	 * because they show the same list — only the frame around it differs.
	 */
	mode = 'pill';

	/**
	 * Which state `docked` was entered from, so dismissing the drawer gives that back.
	 *
	 * The drawer is reached by the panel toggle in the header, which means it is nearly always
	 * entered from the floating window — and collapsing to the pill on dismissal threw that away,
	 * so putting the drawer down cost you the window you had open before it.
	 */
	private dockedFrom = 'pill';

	filter = 'open';
	scope = 'my';
	scopeOpen = false;

	/**
	 * Narrow the list to one record. null is all of them.
	 *
	 * Not persisted, and cleared whenever the scope changes or the record drops out of the list:
	 * a saved narrowing is how somebody comes back tomorrow to an empty widget and concludes it
	 * is broken. The tab and the filter counts are worth remembering; this is a look at one deal.
	 */
	recordFilter = null;

	// The records that actually have something waiting, built in load() rather than derived in a
	// getter — a getter returning a fresh array rebuilds the menu on every change-detection pass,
	// which is what stops a click ever landing on it (the same bug as the automation trigger cards).
	recordOptions: any[] = [];

	/**
	 * Required work that stage moves outran, in the last week.
	 *
	 * Kept out of `rows` on purpose: those are things to act on, and nobody can act on these. A
	 * quote can carry a deal past an unfinished check — required work holds a person, not a quote
	 * — and the row that said "holding this deal" would otherwise just be gone next refresh.
	 */
	/**
	 * The three things you can do to a follow-up without leaving the list: say something back,
	 * acknowledge it, or move it. Reactions are ASCII keys because the database connection is
	 * utf8mb3 and every emoji would collapse to the same '????' — the glyphs are the frontend's
	 * job (CRM-116).
	 */
	readonly reactionGlyphs = { thumbs_up: '👍', celebrate: '🎉', thanks: '🙏', eyes: '👀' };
	readonly reactionOptions = ['thumbs_up', 'celebrate', 'thanks', 'eyes'];

	replyingTo = null;
	replyBody = '';
	reschedulingId = null;
	rescheduleAt = '';

	skipped: any[] = [];
	skippedOpen = false;

	// Dismissed by id, in the browser, because it is a notice rather than a preference: it is
	// answered by having read it, and it should not come back on the next machine as news.
	private skippedSeen: number[] = [];

	// When the list was last fetched, for the "updated just now" line. A timestamp rather than a
	// string, so the label is worked out when it is read rather than going stale where it sits.
	loadedAt = 0;

	// Floating geometry, in pixels. Kept in state rather than in the DOM so it survives a
	// re-render and can be written straight out to storage.
	pos = { x: 0, y: 0 };
	size = { w: 420, h: 520 };
	dockWidth = 420;

	// Where the pill sits. Both axes: it floats over the page rather than belonging to an edge,
	// so it goes wherever suits the screen it is on. {0,0} means "not placed yet" and clamp()
	// puts it in the corner the design draws it in.
	tabPos = { x: 0, y: 0 };

	private dragFrom = null;
	private resizeFrom = null;
	private tabFrom = null;

	/**
	 * Whether the saved state has been read yet.
	 *
	 * The key is per user and per organisation, and the shell knows neither when this component
	 * is constructed — so reading at that moment finds nothing under a placeholder key, and
	 * *writing* at that moment would overwrite the real preferences with defaults. Nothing is
	 * remembered until it has first been restored.
	 */
	private restored = false;

	// Small enough to still be a window, large enough to show a row and its metadata.
	private readonly minSize = { w: 320, h: 260 };

	/**
	 * The pill's size, so clamp() can keep the whole of it on screen.
	 *
	 * Measured rather than assumed: the label is the count, so the pill is a different width with
	 * "All caught up" on it than with "12 Follow-ups", and a guess is the difference between it
	 * sitting against the edge and hanging off it. Cached here rather than read inside clamp(),
	 * which runs on every mousemove of a drag — re-measured when the thing that changes it
	 * changes: the count, the viewport, and picking the pill up.
	 */
	private tabSize = { w: 240, h: 48 };

	readonly filters = [
		{ value: 'all', label: 'All' },
		{ value: 'open', label: 'Open' },
		{ value: 'completed', label: 'Completed' }
	];

	/**
	 * The design's three scopes. All of them stay offered whatever the user's permissions:
	 * `visibility_condition()` already decides what they may see, so for somebody without
	 * can_see_all_sales_data "All team" means everything on the deals they own — which includes
	 * work their colleagues owe, and is not what My attention shows.
	 */
	readonly scopes = [
		{ value: 'my', label: 'My attention' },
		{ value: 'assigned', label: 'Assigned by me' },
		{ value: 'team', label: 'All team' }
	];

	constructor(
		public app: AppService,
		private api: ApiService,
		private router: Router,
		@Inject(DOCUMENT) private document: Document
	) { }

	// --- where it is, and remembering it -------------------------------------

	/**
	 * Per user and per organisation, because the same browser is used by different people and the
	 * same person works in more than one organisation.
	 *
	 * Deliberately the browser rather than the server: a window position from a 27" monitor is
	 * wrong on a laptop, so syncing these between devices would be worse than not. The personal
	 * on/off switch is the one piece of this that does belong to the account, and it arrives with
	 * the Widgets section rather than living here.
	 */
	private get storeKey() {
		return 'wequote-attention:' + (this.app.org || '-') + ':' + (this.app.userId || '-');
	}

	private restore() {
		if (this.restored || !this.app.org) return;
		this.restored = true;

		try {
			const saved = JSON.parse(localStorage.getItem(this.storeKey) || '{}');

			// `docked` is deliberately not restorable. It is a drawer with a backdrop now, and
			// re-opening it on every page load would put that backdrop over whatever the user
			// actually came to do. It is opened on purpose and dismissed when done.
			if (['pill', 'floating'].includes(saved.mode)) this.mode = saved.mode;
			if (['all', 'open', 'completed'].includes(saved.filter)) this.filter = saved.filter;
			if (['my', 'assigned', 'team'].includes(saved.scope)) this.scope = saved.scope;

			if (saved.size) this.size = { ...this.size, ...saved.size };
			if (saved.pos) this.pos = { ...this.pos, ...saved.pos };
			if (saved.dockWidth) this.dockWidth = saved.dockWidth;
			if (saved.tabPos) this.tabPos = { ...this.tabPos, ...saved.tabPos };
			if (Array.isArray(saved.skippedSeen)) this.skippedSeen = saved.skippedSeen;
		} catch (e) {
			// A browser with storage refused, or a value from an older shape. Defaults are fine;
			// what must not happen is the widget failing to appear because of a saved preference.
		}
		this.clamp();
		this.syncDrawer();
	}

	private remember() {
		if (!this.restored) return;

		try {
			localStorage.setItem(this.storeKey, JSON.stringify({
				// Docked is never saved as a resting state, for the same reason it is never
				// restored — the pill is where it goes when it is put away.
				mode: this.mode === 'docked' ? 'pill' : this.mode,
				filter: this.filter, scope: this.scope,
				pos: this.pos, size: this.size, dockWidth: this.dockWidth, tabPos: this.tabPos,
				// Capped: these are ids of rows that stop being returned after a week, so the
				// list can only ever grow by what the window still holds.
				skippedSeen: this.skippedSeen.slice(-200)
			}));
		} catch (e) { /* storage full or refused; the widget still works, it just forgets */ }

		this.syncDrawer();
	}

	/**
	 * Keep the drawer in step with the mode.
	 *
	 * `ui-sidebar` owns whether it is open, so the mode has to be pushed into it rather than read
	 * out of it. Both of its calls no-op when it is already in that state, which is what makes
	 * this safe to run from every hook that can change either side.
	 *
	 * It is called from the route and org-info hooks as well as from setMode, because `available`
	 * waits on both: on a cold load with a saved mode of `docked`, this component's view exists
	 * long before there is an organisation to show.
	 */
	private syncDrawer() {
		if (!this.drawer) return;

		if (!this.available || this.mode !== 'docked') {
			this.drawer.close();
			return;
		}

		// ui-sidebar clears `visible` when its closing animation finishes, and that event can land
		// after a re-open — dismiss the drawer and open it again inside 300ms and `open()` returns
		// early on `isOpen` while `visible` is false, leaving nothing on screen and no way back.
		// reset() clears both flags so the open is honoured. Guarded rather than fixed in the
		// shared component, which every other drawer in the app depends on.
		if (this.drawer.isOpen && !this.drawer.visible) this.drawer.reset();

		this.drawer.open();
	}

	/**
	 * Keep it on screen.
	 *
	 * A window left at the right edge of a wide monitor is off the page entirely on a laptop, and
	 * a saved position is exactly the kind of thing that outlives the screen it was chosen on. Run
	 * on restore and on every viewport resize.
	 */
	private clamp() {
		const vw = window.innerWidth;
		const vh = window.innerHeight;

		this.size.w = Math.max(this.minSize.w, Math.min(this.size.w, vw - 24));
		this.size.h = Math.max(this.minSize.h, Math.min(this.size.h, vh - 24));
		this.dockWidth = Math.max(this.minSize.w, Math.min(this.dockWidth, vw - 24));

		// Default the floating window to the bottom-right, where the pill was.
		if (!this.pos.x && !this.pos.y) {
			this.pos = { x: vw - this.size.w - 16, y: vh - this.size.h - 72 };
		}

		this.pos.x = Math.max(8, Math.min(this.pos.x, vw - this.size.w - 8));
		this.pos.y = Math.max(8, Math.min(this.pos.y, vh - this.size.h - 8));

		// The pill. Bottom-right to start with, clear of the chat launcher in the other corner,
		// and kept fully on screen after that — a position chosen on a wide monitor is off the
		// page entirely on a laptop.
		if (!this.tabPos.x && !this.tabPos.y) {
			this.tabPos = { x: vw - this.tabSize.w - 16, y: vh - this.tabSize.h - 24 };
		}
		this.tabPos.x = Math.max(8, Math.min(this.tabPos.x, vw - this.tabSize.w - 8));
		this.tabPos.y = Math.max(8, Math.min(this.tabPos.y, vh - this.tabSize.h - 8));
	}

	// Read the pill's real size. Silent when it is not on screen — the last measurement, or the
	// default, is the best answer available then.
	private measureTab() {
		const el = this.tabEl?.nativeElement;
		if (el?.offsetWidth) this.tabSize = { w: el.offsetWidth, h: el.offsetHeight };
	}

	@HostListener('window:resize')
	onWindowResize() {
		this.measureTab();
		this.clamp();
		this.remember();
	}

	// --- the three states ----------------------------------------------------

	setMode(mode) {
		// Noted on the way in, because on the way out the drawer only knows it was dismissed.
		if (mode === 'docked' && this.mode !== 'docked') this.dockedFrom = this.mode;

		this.mode = mode;
		this.clamp();
		this.remember();
		if (mode !== 'pill' && !this.loaded) this.load();
	}

	/**
	 * The drawer was dismissed by its backdrop — the only route that reaches this, since
	 * `ui-sidebar` listens for `window:resize` and nothing else, so Escape does not close it (true
	 * of every drawer in the app, not something this one broke).
	 *
	 * Back to whatever it was docked from, which is the floating window in every route the header
	 * offers. It used to go to the pill on the reasoning that a drawer is opened to work through
	 * the list and then put away — but dismissing the drawer is not asking to put the widget away,
	 * and it cost people the window they had open before they docked it. Minimise is a separate
	 * control and still means the pill: it sets the mode itself, so the guard below sees `pill`
	 * already and leaves it alone.
	 *
	 * `pill` if it was somehow docked from docked, which is the old behaviour and the safe floor.
	 */
	onDrawerClosed() {
		if (this.mode !== 'docked') return;
		this.mode = this.dockedFrom === 'docked' ? 'pill' : this.dockedFrom;
		this.clamp();
		this.remember();
	}

	/**
	 * The pill's + reaches this without expanding anything, which the design requires: it is the
	 * one control that is there whatever the list says, including "All caught up".
	 */
	create() {
		this.composer?.open();
	}

	setFilter(value) {
		this.filter = value;
		this.remember();
	}

	setRecordFilter(id) {
		this.recordFilter = id;
	}

	// Read and put away. The deal's own stage history keeps the record for good, so nothing is
	// lost by clearing it here.
	dismissSkipped() {
		this.skippedSeen = this.skippedSeen.concat(this.skipped.map(s => Number(s.id)));
		this.skipped = [];
		this.skippedOpen = false;
		this.remember();
	}

	// --- moving and sizing it ------------------------------------------------

	/**
	 * Drag by the header, but not by the controls inside it.
	 *
	 * Listeners go on the document rather than the element, so the pointer can leave the window
	 * mid-drag without the drag stopping — which is what happens the moment somebody moves it
	 * quickly. They are removed on release rather than living for the component's lifetime.
	 */
	startDrag(event) {
		if (this.mode !== 'floating') return;
		if (event.target.closest('button, .crm-att-scope')) return;

		event.preventDefault();
		this.dragFrom = { x: event.clientX - this.pos.x, y: event.clientY - this.pos.y };
		this.listenWhileHeld();
	}

	/**
	 * Drag the pill anywhere on the page.
	 *
	 * The pill is also a button, so the drag has to not eat the click. The threshold below decides
	 * between them on release — under it, this was somebody pressing the pill to open the list.
	 */
	startTabDrag(event) {
		if (event.target.closest('button')) return;

		event.preventDefault();
		this.measureTab();
		this.tabFrom = {
			x: event.clientX - this.tabPos.x, y: event.clientY - this.tabPos.y,
			moved: false, startX: event.clientX, startY: event.clientY
		};
		this.listenWhileHeld();
	}

	startResize(event) {
		event.preventDefault();
		event.stopPropagation();
		this.resizeFrom = {
			x: event.clientX, y: event.clientY,
			w: this.mode === 'docked' ? this.dockWidth : this.size.w,
			h: this.size.h
		};
		this.listenWhileHeld();
	}

	private listenWhileHeld() {
		const move = e => {
			if (this.tabFrom) {
				this.tabPos = { x: e.clientX - this.tabFrom.x, y: e.clientY - this.tabFrom.y };
				if (Math.abs(e.clientX - this.tabFrom.startX) > 4
					|| Math.abs(e.clientY - this.tabFrom.startY) > 4) this.tabFrom.moved = true;
			} else if (this.dragFrom) {
				this.pos = { x: e.clientX - this.dragFrom.x, y: e.clientY - this.dragFrom.y };
			} else if (this.resizeFrom) {
				if (this.mode === 'docked') {
					// The docked panel grows leftwards, so the edge moving left makes it wider.
					this.dockWidth = this.resizeFrom.w - (e.clientX - this.resizeFrom.x);
				} else {
					this.size = {
						w: this.resizeFrom.w + (e.clientX - this.resizeFrom.x),
						h: this.resizeFrom.h + (e.clientY - this.resizeFrom.y)
					};
				}
			}
			this.clamp();
		};

		const up = () => {
			// A press that did not move is the pill being used as the button it also is.
			if (this.tabFrom && !this.tabFrom.moved) this.setMode('floating');

			this.tabFrom = null;
			this.dragFrom = null;
			this.resizeFrom = null;
			document.removeEventListener('mousemove', move);
			document.removeEventListener('mouseup', up);
			this.remember();
		};

		document.addEventListener('mousemove', move);
		document.addEventListener('mouseup', up);
	}

	// --- reading -------------------------------------------------------------

	/**
	 * Whether this user may have the widget at all.
	 *
	 * `crmEnabled` is the app's own rule — allow_crm on the organisation, access_crm on the role,
	 * with the root bypass that mirrors Role::check — and four screens already ask it. This is
	 * the fifth caller, not a second copy.
	 *
	 * The switch in Customise your workspace comes last: permission decides whether it can be
	 * offered, the switch only whether this person wants it.
	 */
	get available() {
		return !!this.app.org && !!this.app.routeData?.auth
			&& this.app.crmEnabled && this.app.attentionWidgetEnabled;
	}

	/**
	 * How long ago the list was fetched, in the words the design uses.
	 *
	 * Deliberately coarse. This line answers "is what I am looking at current?", and a ticking
	 * count of seconds invites reading it as a clock, which it is not.
	 */
	get freshness() {
		if (!this.loadedAt) return 'never';

		const minutes = Math.floor((Date.now() - this.loadedAt) / 60000);

		if (minutes < 1) return 'just now';
		if (minutes === 1) return '1 min ago';
		if (minutes < 60) return minutes + ' min ago';

		const hours = Math.floor(minutes / 60);
		return hours === 1 ? '1 hour ago' : hours + ' hours ago';
	}

	/**
	 * What the pill says, which for most of the day is the whole of this widget.
	 *
	 * "All caught up" has to mean the list was read and was empty. A first load that failed left
	 * the count at zero and said it anyway — the one place the false negative did real harm,
	 * since nobody opens a panel that has just told them there is nothing in it.
	 *
	 * "waiting" rather than "Follow-ups": the count is of open rows, and some of those are
	 * required work, which is the one thing this widget is careful not to call a follow-up
	 * anywhere else. It also pairs with the zero state, which is about being caught up rather
	 * than about having no follow-ups. Not "items" — the panel uses that for the whole list
	 * including what is already done, and these are only the ones still owed.
	 */
	get pillLabel() {
		if (this.failed && !this.loadedAt) return 'Couldn\'t load';
		if (this.counts.open) return this.counts.open + ' waiting';
		return 'All caught up';
	}

	// The notice in the panel, which says which of the two failures this is: nothing to fall back
	// on, or a list on screen that is no longer current.
	get failedLabel() {
		return this.loadedAt ? 'Couldn\'t refresh this list' : 'Couldn\'t load your list';
	}

	get scopeLabel() {
		return this.scopes.find(s => s.value === this.scope)?.label;
	}

	// Everything the record filter allows, whatever the All/Open/Completed tab says. The tab
	// counts are taken from this, so they describe the list actually on screen.
	get scopedRows() {
		if (!this.recordFilter) return this.rows;
		return this.rows.filter(r => Number(r.crm_id) === Number(this.recordFilter));
	}

	get visibleRows() {
		if (this.filter === 'all') return this.scopedRows;
		return this.scopedRows.filter(r => r.status === this.filter);
	}

	get recordFilterLabel() {
		if (!this.recordFilter) return 'All records';
		return this.recordOptions.find(r => Number(r.id) === Number(this.recordFilter))?.title
			|| 'All records';
	}

	/**
	 * Counted here rather than taken from the response.
	 *
	 * The server counts everything this scope can see, which is the right answer until somebody
	 * narrows to one deal — then a tab reading 13 above a list of 2 is the widget contradicting
	 * itself. Counting what is in hand cannot disagree with what is drawn.
	 */
	countFor(value) {
		const rows = this.scopedRows;
		if (value === 'all') return rows.length;
		return rows.filter(r => r.status === value).length;
	}

	ngOnInit() {
		this.restore();
		if (this.available) this.load();

		/**
		 * The shell boots before it knows which organisation this is, so on a cold load
		 * `available` is false here and the pill would sit at "All caught up" for ever, having
		 * decided once and never looked again. This is also the first moment the saved state can
		 * be read, because its key is per organisation and per user.
		 */
		this.app.onOrgInfoChange.subscribe(() => {
			this.restore();
			// Also the moment the Widgets switch flips: a drawer left open by somebody who has
			// just turned the widget off has to close with it.
			this.syncDrawer();
			if (this.available && !this.loaded) this.load();
		});

		// The list is about records, not about the page — but moving between screens is the
		// moment somebody has probably just changed one of these things. Navigating away also
		// closes the drawer, since a backdrop that outlived the screen it was opened over would
		// be sitting on top of a page nobody asked it to cover.
		this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe(() => {
			if (this.mode === 'docked') this.setMode('pill');
			if (this.available) this.load();
		});

		// Something on a screen has written one of these. Coalesced rather than answered
		// immediately: completing a follow-up that resolves required work is two writes and one
		// change, and the activity panel saves and then reloads its own feed.
		this.app.onAttentionChange.subscribe(() => this.reloadSoon());

		/**
		 * Everything this browser did not do: an automation firing from the queue, a colleague
		 * working the same deals, the Snagg app closing something off. There is no signal to
		 * subscribe to for any of it, so the list is re-read on a timer.
		 *
		 * Slower when collapsed than when open, and stopped entirely while the browser tab is in
		 * the background — the same guard the schedule calendar's auto-refresh uses. A widget that
		 * kept polling in twenty forgotten tabs would be the most expensive thing on the page.
		 */
		this.pollTimer = setInterval(() => {
			if (this.document.hidden || this.disabled || !this.available) return;
			if (this.mode === 'pill' && Date.now() - (this.loadedAt || 0) < this.PILL_POLL_MS) return;
			this.load();
		}, this.OPEN_POLL_MS);
	}

	ngOnDestroy() {
		if (this.pollTimer) clearInterval(this.pollTimer);
		if (this.reloadTimer) clearTimeout(this.reloadTimer);
	}

	/**
	 * Re-read shortly, once, however many times this is asked.
	 *
	 * The window between the write and the read is deliberate as well as coalescing: required
	 * work is resolved by the same call that completes the follow-up it hangs off, and reading
	 * back before the server has finished both would show the half of it that landed first.
	 */
	private reloadSoon(delay = 400) {
		if (!this.available) return;
		if (this.reloadTimer) clearTimeout(this.reloadTimer);
		this.reloadTimer = setTimeout(() => { this.reloadTimer = null; this.load(); }, delay);
	}

	/**
	 * Let a row be seen being completed before the list takes it away.
	 *
	 * Marking something done reloaded at once, so the row vanished from under the cursor with
	 * nothing to say whether it had been completed or had merely stopped matching the filter.
	 * It now stays for a moment wearing the tick and the strikethrough a completed row already
	 * wears — the styling is the list's own, not a special case — and the toast says where it has
	 * gone, because the Completed tab is not in view when you are looking at Open.
	 *
	 * The row is marked rather than its status rewritten: status is the server's word for what
	 * this is, and this is the screen pausing on its way to asking again.
	 */
	private settle(row, message) {
		row.settled = true;
		this.app.notifications.showSuccess(message);
		this.reloadSoon(this.SETTLE_MS);
	}

	load() {
		this.api.crm.attention(this.scope, data => {
			this.rows = data.rows || [];
			this.counts = data.counts || { all: 0, open: 0, completed: 0 };
			this.loaded = true;
			this.failed = false;
			this.loadedAt = Date.now();
			this.buildRecordOptions();
			// Only what has not already been read. Filtered here rather than in the query: the
			// dismissals live in this browser, and the server has no business knowing them.
			this.skipped = (data.skipped || []).filter(s => !this.skippedSeen.includes(Number(s.id)));

			// The count is the tab's label, so it has just changed height. Measured after the view
			// has caught up with the new text, or it measures the old one.
			setTimeout(() => { this.measureTab(); this.clamp(); });
		}, () => {
			// Still quiet — this is a widget on somebody else's screen, and a CRM that is
			// unreachable should not put an error over whatever they were actually doing. Said in
			// the panel instead of nowhere: what it must not do is leave a zero on screen looking
			// like an answer. Anything already read stays up, because a list from a minute ago is
			// worth more than a blank panel, and the notice says it is not current.
			this.loaded = true;
			this.failed = true;
		});
	}

	setScope(value) {
		this.scope = value;
		this.scopeOpen = false;
		// A different scope is a different set of records, and the one being looked at may not be
		// in it. Starting wide is the only answer that cannot show nothing.
		this.recordFilter = null;
		this.remember();
		this.load();
	}

	/**
	 * The records with something waiting on them, each with how much.
	 *
	 * Built from the rows rather than fetched: a list of every deal would mostly be deals with
	 * nothing to show, and this menu exists to get to the one that does.
	 */
	private buildRecordOptions() {
		const byId = new Map();

		this.rows.forEach(row => {
			const found = byId.get(row.crm_id);
			if (found) { found.count++; return; }
			byId.set(row.crm_id, { id: row.crm_id, title: row.record_title, count: 1 });
		});

		this.recordOptions = [...byId.values()]
			.sort((a, b) => (a.title || '').localeCompare(b.title || ''));

		// The deal being looked at may have just had its last item resolved, or gone out of scope
		// with a reload. Silently widening beats a filter naming something that is not there.
		if (this.recordFilter && !byId.has(Number(this.recordFilter))) this.recordFilter = null;
	}

	// *ngFor identity for both lists. Without it every change-detection pass tears the rows down
	// and rebuilds them, and a mousedown and mouseup then land on two different elements — so the
	// click never happens.
	trackRow(index, row) {
		return row.kind + ':' + row.id;
	}

	trackRecord(index, record) {
		return record.id;
	}

	// --- how a row reads -----------------------------------------------------

	/**
	 * Overdue, due, completed — as a word as well as a colour, because the design's own rule is
	 * that status is never carried by colour alone.
	 */
	timing(row) {
		if (row.status === 'completed') return { label: 'Completed', className: 'text-forestgreen text-bold' };
		if (row.kind === 'required') return { label: 'Holding this deal', className: 'text-crimson text-bold' };
		if (!row.due) return { label: 'No date', className: 'text-secondary' };

		if (new Date(String(row.due).replace(' ', 'T')) >= new Date()) {
			return { label: 'Due', className: 'text-ochre text-bold' };
		}

		// A meeting is an event, not a task. It happened in the world or it did not, and ticking
		// it off here is bookkeeping — so "Overdue" accuses somebody of being late for something
		// that may well be finished, and offers a checkbox that says the wrong thing either way.
		// Ask instead. Answering yes is the same tick; answering no is rescheduling or removing
		// the meeting on the record, which is where both already live.
		if (row.activity_type === 'meeting') {
			return { label: 'Did this happen?', className: 'text-royalblue text-bold' };
		}

		return { label: 'Overdue', className: 'text-crimson text-bold' };
	}

	sourceLabel(row) {
		if (row.source_type === 'mention') return 'Mention';
		if (row.source_type === 'meeting') return 'Meeting';
		// A file request is required work that cannot be finished by saying so, and the deal's
		// own Focus panel names it separately for that reason. Named the same here.
		if (row.source_type === 'required') return row.resolve_mode === 'file' ? 'File request' : (this.isQuestion(row) ? 'Question' : 'Required');
		return 'Note';
	}

	iconFor(row) {
		if (row.kind === 'required') return 'wq wq-lock-closed';
		if (row.source_type === 'meeting') return 'wq wq-schedule';
		if (row.source_type === 'mention') return 'wq wq-person';
		return 'wq wq-notes';
	}

	// --- acting on one -------------------------------------------------------

	/**
	 * Required work is resolved, not completed, and the server refuses a `file` one until a file
	 * is attached — so the refusal is shown rather than swallowed. Everything else completes.
	 */
	toggle(row, event) {
		event.stopPropagation();
		if (this.disabled) return;

		if (row.settled) return;
		if (row.kind === 'required') { this.resolve(row); return; }
		if (row.status === 'completed') { this.reopen(row); return; }

		this.disabled = true;
		this.api.crm.completeFollowUp(row.id, () => {
			this.disabled = false;
			this.settle(row, 'Marked complete — moved to Completed.');
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	// The site-visit question, or one of the organisation's own: answered Yes or No, never ticked.
	isQuestion(row) {
		return row.kind === 'required' && (row.resolve_mode === 'answer' || row.resolve_mode === 'question');
	}

	// Answered in place. Its branch can move the deal, so a screen showing that deal is told too.
	answer(row, answer: boolean, event) {
		event.stopPropagation();
		if (this.disabled || row.settled) return;
		this.disabled = true;

		const data = { crm_id: row.crm_id, id: row.id, answer: answer ? 1 : 0 };
		const send = row.resolve_mode === 'question' ? this.api.crm.answerQuestion : this.api.crm.answerSiteVisit;
		send(data, () => {
			this.disabled = false;
			this.app.onAttentionChange.emit(Number(row.crm_id));
			this.settle(row, 'Answered ' + (answer ? 'Yes' : 'No') + '.');
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	resolve(row) {
		// Said before the press rather than after it: a file request is refused server-side until
		// the file is on the deal, and finding that out only once you have confirmed is the widget
		// wasting your time. The deal's own panel says the same thing on the row itself.
		const detail = row.resolve_mode === 'file'
			? ' The file has to be on the deal first — attach it there, then mark this done.'
			: '';

		this.app.dialog.pop('Mark as done',
			'"' + row.title + '" is holding ' + (row.record_title || 'this deal') + '. Marking it '
			+ 'done says somebody has looked and it is finished.' + detail,
			['Cancel', '*Mark as done'], btn => {
				if (btn !== 1) return;
				this.disabled = true;
				this.api.crm.resolveRequirement({ id: row.id }, () => {
					this.disabled = false;
					this.settle(row, 'Marked as done — moved to Completed.');
				}, error => {
					this.disabled = false;
					this.app.notifications.showDanger(error.message);
				});
			});
	}

	/**
	 * Reopening something whose time has passed needs a new one.
	 *
	 * The design's rule: a future follow-up can simply be reopened, but one that has already come
	 * due would return to the list already overdue, which is not a decision anybody made — so it
	 * opens the row's reschedule instead of reopening it on a date that has passed.
	 */
	reopen(row) {
		const past = row.due && new Date(String(row.due).replace(' ', 'T')) < new Date();

		// Reopening something already past its time needs a new one, or it would come back to the
		// list overdue — which is not a decision anybody made. That used to mean a trip to the
		// record; the row's own picker answers it in place now.
		if (past) {
			this.startReschedule(row);
			return;
		}

		this.disabled = true;
		this.api.crm.reopenFollowUp(row.id, () => {
			this.disabled = false;
			this.load();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	// --- reply, react, reschedule (CRM-116's three, on this list) -------------
	//
	// Every one of these sits inside a row whose own click opens the record, so each stops the
	// event. Only a follow-up gets them: required work is a requirement rather than an activity,
	// with nothing to react to and nobody to reply to.

	canAct(row) {
		return row.kind === 'follow_up';
	}

	glyph(key) {
		return this.reactionGlyphs[key] || key;
	}

	reactionList(row) {
		const reactions = row.reactions || {};
		return Object.keys(reactions).map(key => ({ key, count: (reactions[key] || []).length }));
	}

	hasReacted(row, key) {
		return ((row.reactions || {})[key] || []).map(Number).includes(Number(this.app.userId));
	}

	// The members past the first few, for the "+N" tooltip on a meeting row.
	memberNames(members: any[], from: number) {
		return (members || []).slice(from).map(m => m.name).join(', ');
	}

	react(row, key, event?) {
		if (event) event.stopPropagation();
		// Written back onto the row rather than reloaded: the answer is the new blob, and a
		// reload here would move the list under somebody mid-click.
		this.api.crm.react({ activity_id: row.id, reaction: key }, reactions => {
			row.reactions = reactions || {};
		}, error => this.app.notifications.showDanger(error.message));
	}

	startReply(row, event?) {
		if (event) event.stopPropagation();
		this.cancelReschedule();
		this.replyingTo = row.id;
		this.replyBody = '';
	}

	cancelReply(event?) {
		if (event) event.stopPropagation();
		this.replyingTo = null;
		this.replyBody = '';
	}

	saveReply(row, event?) {
		if (event) event.stopPropagation();
		const body = (this.replyBody || '').trim();
		if (!body) {
			this.app.notifications.showDanger('Please write a reply.');
			return;
		}

		this.disabled = true;
		// A reply is an activity with a parent, the same shape the record's own timeline writes.
		// Escaped rather than trusted: this box takes plain text, not markup.
		this.api.crm.saveActivity({
			id: 'new',
			crm_id: row.crm_id,
			activity_type: 'note',
			body: '<p>' + body.replace(/</g, '&lt;') + '</p>',
			parent_id: row.id
		}, () => {
			this.disabled = false;
			this.cancelReply();
			this.load();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	startReschedule(row, event?) {
		if (event) event.stopPropagation();
		this.cancelReply();
		this.reschedulingId = row.id;
		// Opens on the time it already has, so moving it by an hour is an edit rather than a
		// re-entry. datetime-local wants a T and no seconds.
		this.rescheduleAt = row.due ? String(row.due).replace(' ', 'T').slice(0, 16) : '';
	}

	cancelReschedule(event?) {
		if (event) event.stopPropagation();
		this.reschedulingId = null;
		this.rescheduleAt = '';
	}

	saveReschedule(row, event?) {
		if (event) event.stopPropagation();
		if (!this.rescheduleAt) {
			this.app.notifications.showDanger('Please choose a new date and time.');
			return;
		}

		this.disabled = true;
		this.api.crm.rescheduleActivity({
			activity_id: row.id,
			follow_up_datetime: this.rescheduleAt.replace('T', ' ') + ':00'
		}, () => {
			this.disabled = false;
			this.cancelReschedule();
			this.load();
		}, error => {
			this.disabled = false;
			this.app.notifications.showDanger(error.message);
		});
	}

	/**
	 * Open the record this belongs to, at the entry itself.
	 *
	 * The deal workspace already scrolls to an activity and highlights it; passing the id in the
	 * address means reusing that rather than building a second way to point at a row.
	 */
	// A skipped item points at its record like any row does, minus the activity: there is nothing
	// on the history to highlight, the point is the deal it happened on.
	goToSkipped(item) {
		this.goTo({ record_type: item.record_type, crm_id: item.crm_id, kind: 'required', id: null });
	}

	goTo(row) {
		const activity = row.kind === 'required' ? row.crm_activity_id : row.id;
		const queryParams: any = {};
		if (activity) queryParams.activity = activity;

		// A deal has a page; a lead is a modal over the leads list, so it is asked for by query
		// rather than by address. `/crm/leads/<id>` is not a route, and navigating to it landed
		// on nothing at all.
		if (row.record_type === 'lead') {
			this.router.navigate(['/', this.app.org, 'crm', 'leads'],
				{ queryParams: { ...queryParams, lead: row.crm_id } });
			return;
		}

		this.router.navigate(['/', this.app.org, 'crm', 'deals', row.crm_id], { queryParams });
	}

}
